import Decimal from 'decimal.js';
import { escapeCsvField } from './csvExport';
import { TAX_PIPELINE } from './taxPipelineConfig';
import { isTaxableIncomeLedgerEvent } from './fiIncomeReport';
import type { FiatCashEvent, LedgerEvent } from './types';

/** All-time fiat cash rows from CDC cash CSV (non-tax, non-FIFO). */
export function buildFiFiatReconciliationAllTimeCsv(fiat: FiatCashEvent[]): string {
  const meta = TAX_PIPELINE.fi;
  const headers = [
    'policy_mode',
    'basis_method',
    'timezone',
    'timestamp_utc',
    'tax_year',
    'fiat_subkind',
    'source_type',
    'transaction_description',
    'currency',
    'amount',
    'to_currency',
    'to_amount',
    'native_currency',
    'native_amount',
    'native_amount_usd',
    'eur_reconciliation',
    'reference',
    'rationale',
    'source_platform',
  ];
  const sorted = [...fiat].sort((a, b) => a.ts - b.ts);
  const lines = [headers.map(escapeCsvField).join(',')];
  for (const f of sorted) {
    const iso = new Date(f.ts).toISOString();
    const eur =
      f.eurReconciliation != null && f.eurReconciliation.toFixed != null
        ? f.eurReconciliation.toFixed(8)
        : '';
    lines.push(
      [
        meta.policyMode,
        meta.basisMethod,
        meta.timezone,
        iso,
        String(f.taxYear),
        f.fiatSubkind,
        f.sourceType,
        f.transactionDescription,
        f.currency,
        f.amount,
        f.toCurrency,
        f.toAmount,
        f.nativeCurrency,
        f.nativeAmount,
        f.nativeAmountUsd,
        eur,
        f.reference,
        f.rationale,
        f.sourcePlatform,
      ]
        .map(escapeCsvField)
        .join(',')
    );
  }
  return lines.join('\r\n');
}

export type FiAuditReconciliationTotals = {
  fiatDepositsEur: Decimal;
  fiatWithdrawalsEur: Decimal;
  fiatCardTopUpEur: Decimal;
  cryptoIncomeFmvEur: Decimal;
  cryptoDisposalProceedsEur: Decimal;
  cryptoAcquisitionBasisEur: Decimal;
};

/**
 * Lightweight audit totals: fiat from `FiatCashEvent`; crypto from `LedgerEvent` only (FIFO universe).
 */
export function computeFiAuditReconciliationTotals(
  ledgerEvents: LedgerEvent[],
  fiatCash: FiatCashEvent[]
): FiAuditReconciliationTotals {
  let fiatDepositsEur = new Decimal(0);
  let fiatWithdrawalsEur = new Decimal(0);
  let fiatCardTopUpEur = new Decimal(0);

  for (const f of fiatCash) {
    const e = f.eurReconciliation;
    if (e == null || !e.gt(0)) continue;
    const x = e.abs();
    if (f.fiatSubkind === 'DEPOSIT') fiatDepositsEur = fiatDepositsEur.plus(x);
    else if (f.fiatSubkind === 'WITHDRAWAL') fiatWithdrawalsEur = fiatWithdrawalsEur.plus(x);
    else if (f.fiatSubkind === 'CARD_TOP_UP') fiatCardTopUpEur = fiatCardTopUpEur.plus(x);
  }

  let cryptoIncomeFmvEur = new Decimal(0);
  let cryptoDisposalProceedsEur = new Decimal(0);
  let cryptoAcquisitionBasisEur = new Decimal(0);

  for (const e of ledgerEvents) {
    if (e.kind === 'internal_transfer') continue;
    if (e.eurValue == null || !e.eurValue.gt(0)) continue;
    const v = e.eurValue;
    if (isTaxableIncomeLedgerEvent(e)) {
      cryptoIncomeFmvEur = cryptoIncomeFmvEur.plus(v);
    } else if (e.kind === 'disposal') {
      cryptoDisposalProceedsEur = cryptoDisposalProceedsEur.plus(v);
    } else if (e.kind === 'acquisition') {
      cryptoAcquisitionBasisEur = cryptoAcquisitionBasisEur.plus(v);
    }
  }

  return {
    fiatDepositsEur,
    fiatWithdrawalsEur,
    fiatCardTopUpEur,
    cryptoIncomeFmvEur,
    cryptoDisposalProceedsEur,
    cryptoAcquisitionBasisEur,
  };
}

/** Aggregates for fiat CSV + optional yearly breakdown. */
export function buildFiFiatSummaryCsv(fiat: FiatCashEvent[]): string {
  const meta = TAX_PIPELINE.fi;
  const lines: string[] = [];
  const push = (cells: string[]) => {
    if (cells.length !== 8) {
      throw new Error(`fiat summary CSV row must have 8 columns, got ${cells.length}`);
    }
    lines.push(cells.map(escapeCsvField).join(','));
  };

  push([
    'policy_mode',
    'basis_method',
    'timezone',
    'section',
    'category',
    'key',
    'value_eur_or_count',
    'note',
  ]);
  push([
    meta.policyMode,
    meta.basisMethod,
    meta.timezone,
    'meta',
    'pipeline',
    'fiat_only_rows',
    String(fiat.length),
    'CDC cash fiat rows (not FIFO)',
  ]);

  const bySub = new Map<string, { count: number; sumEur: Decimal }>();
  const byCur = new Map<string, { count: number; sumEur: Decimal }>();
  const byYear = new Map<number, { count: number; sumEur: Decimal }>();

  for (const f of fiat) {
    const sub = f.fiatSubkind;
    const cur = f.currency || '(empty)';
    const yr = f.taxYear;
    const e = f.eurReconciliation;
    const add = (m: Map<string, { count: number; sumEur: Decimal }>, k: string) => {
      const o = m.get(k) ?? { count: 0, sumEur: new Decimal(0) };
      o.count++;
      if (e != null && e.gt(0)) o.sumEur = o.sumEur.plus(e.abs());
      m.set(k, o);
    };
    add(bySub, sub);
    add(byCur, cur);
    const yEnt = byYear.get(yr) ?? { count: 0, sumEur: new Decimal(0) };
    yEnt.count++;
    if (e != null && e.gt(0)) yEnt.sumEur = yEnt.sumEur.plus(e.abs());
    byYear.set(yr, yEnt);
  }

  push([
    meta.policyMode,
    meta.basisMethod,
    meta.timezone,
    'summary',
    'deposits',
    'total_eur_reconciliation',
    fiatDepositTotal(fiat).toFixed(2),
    'viban_deposit',
  ]);
  push([
    meta.policyMode,
    meta.basisMethod,
    meta.timezone,
    'summary',
    'withdrawals',
    'total_eur_reconciliation',
    fiatWithdrawalTotal(fiat).toFixed(2),
    'viban_withdrawal',
  ]);
  push([
    meta.policyMode,
    meta.basisMethod,
    meta.timezone,
    'summary',
    'card_top_ups',
    'total_eur_reconciliation',
    fiatCardTopUpTotal(fiat).toFixed(2),
    'viban_card_top_up',
  ]);
  push([
    meta.policyMode,
    meta.basisMethod,
    meta.timezone,
    'summary',
    'all_rows',
    'row_count',
    String(fiat.length),
    'all fiat-only rows',
  ]);

  for (const [k, v] of Array.from(bySub.entries()).sort((a, b) => a[0].localeCompare(b[0]))) {
    push([
      meta.policyMode,
      meta.basisMethod,
      meta.timezone,
      'by_fiat_subkind',
      k,
      'count',
      String(v.count),
      '',
    ]);
    push([
      meta.policyMode,
      meta.basisMethod,
      meta.timezone,
      'by_fiat_subkind',
      k,
      'sum_eur_reconciliation',
      v.sumEur.toFixed(2),
      '',
    ]);
  }

  for (const [k, v] of Array.from(byCur.entries()).sort((a, b) => a[0].localeCompare(b[0]))) {
    push([
      meta.policyMode,
      meta.basisMethod,
      meta.timezone,
      'by_currency',
      k,
      'count',
      String(v.count),
      '',
    ]);
    push([
      meta.policyMode,
      meta.basisMethod,
      meta.timezone,
      'by_currency',
      k,
      'sum_eur_reconciliation',
      v.sumEur.toFixed(2),
      '',
    ]);
  }

  for (const [yr, v] of Array.from(byYear.entries()).sort((a, b) => a[0] - b[0])) {
    push([
      meta.policyMode,
      meta.basisMethod,
      meta.timezone,
      'by_tax_year_utc',
      String(yr),
      'count',
      String(v.count),
      '',
    ]);
    push([
      meta.policyMode,
      meta.basisMethod,
      meta.timezone,
      'by_tax_year_utc',
      String(yr),
      'sum_eur_reconciliation',
      v.sumEur.toFixed(2),
      '',
    ]);
  }

  push([
    meta.policyMode,
    meta.basisMethod,
    meta.timezone,
    'counts_by_source_type',
    'distinct_transaction_kinds',
    'count',
    String(new Set(fiat.map((f) => f.sourceType)).size),
    'distinct Transaction Kind values',
  ]);
  for (const t of Array.from(new Set(fiat.map((f) => f.sourceType))).sort()) {
    const n = fiat.filter((f) => f.sourceType === t).length;
    push([
      meta.policyMode,
      meta.basisMethod,
      meta.timezone,
      'source_type',
      t,
      'row_count',
      String(n),
      '',
    ]);
  }

  return lines.join('\r\n');
}

function fiatDepositTotal(fiat: FiatCashEvent[]): Decimal {
  let s = new Decimal(0);
  for (const f of fiat) {
    if (f.fiatSubkind !== 'DEPOSIT' || f.eurReconciliation == null || !f.eurReconciliation.gt(0)) continue;
    s = s.plus(f.eurReconciliation.abs());
  }
  return s;
}

function fiatWithdrawalTotal(fiat: FiatCashEvent[]): Decimal {
  let s = new Decimal(0);
  for (const f of fiat) {
    if (f.fiatSubkind !== 'WITHDRAWAL' || f.eurReconciliation == null || !f.eurReconciliation.gt(0)) continue;
    s = s.plus(f.eurReconciliation.abs());
  }
  return s;
}

function fiatCardTopUpTotal(fiat: FiatCashEvent[]): Decimal {
  let s = new Decimal(0);
  for (const f of fiat) {
    if (f.fiatSubkind !== 'CARD_TOP_UP' || f.eurReconciliation == null || !f.eurReconciliation.gt(0)) continue;
    s = s.plus(f.eurReconciliation.abs());
  }
  return s;
}
